<script lang="ts">
	import { currencySymbol } from '$lib';

	let {
		min = 0,
		max = 100,
		minValue,
		maxValue,
		onchange: parentOnChange,
	}: {
		min: number;
		max: number;
		minValue: number;
		maxValue: number;
		onchange?: (min: number, max: number) => void;
	} = $props();

	let toSlider = $state<HTMLInputElement>();
	let clientWidth = $state(0);
	let minGap = $derived(Math.round(((max - min) / clientWidth) * 15));

	const updateSliderBackground = (minValue: number, maxValue: number) => {
		if (toSlider === undefined) return;

		const rangeDistance = max - min;

		const left = Math.max(((minValue - min) / rangeDistance) * 100, 0);
		const right = Math.min(((maxValue - min) / rangeDistance) * 100, 100);
		toSlider.style.background = `linear-gradient(to right, #aaaaaa 0%, #aaaaaa ${left}%, var(--sliders-color) ${left}%, var(--sliders-color) ${right}%, #aaaaaa ${right}%, #aaaaaa 100%`;
	};

	const onSliderInput = (type: 'min' | 'max') => {
		if (type === 'min') {
			minValue = Math.min(minValue, maxValue - minGap);
		} else {
			maxValue = Math.max(maxValue, minValue + minGap);
		}

		if (minValue < min) minValue = min;
		if (maxValue > max) maxValue = max;
	};

	const onInputChange = (
		type: 'min' | 'max',
		e: Event & { currentTarget: EventTarget & HTMLInputElement },
	) => {
		const value = Number(e.currentTarget.value);
		if (type === 'min') {
			if (value < min) {
				minValue = min;
			} else if (value > maxValue - minGap) {
				minValue = maxValue - minGap;
			} else {
				minValue = value;
			}
			e.currentTarget.value = minValue.toString();
		} else {
			if (value > max) {
				maxValue = max;
			} else if (value < minValue + minGap) {
				maxValue = minValue + minGap;
			} else {
				maxValue = value;
			}
			e.currentTarget.value = maxValue.toString();
		}
		updateSliderBackground(minValue, maxValue);
		onChange();
	};

	const onChange = () => {
		parentOnChange?.(minValue, maxValue);
	};

	const currency = currencySymbol();

	$effect(() => {
		updateSliderBackground(minValue, maxValue);
	});
</script>

<div class="mb-3 flex justify-around">
	<div class="flex items-center">
		{#if currency.position === 'left'}
			{@render symbol()}
		{/if}
		<input
			type="number"
			onchange={(e) => onInputChange('min', e)}
			step={1}
			{min}
			max={maxValue - 1}
			value={minValue}
			class="w-20 rounded border border-neutral-300 p-1"
		/>
		{#if currency.position === 'right'}
			{@render symbol()}
		{/if}
	</div>

	<div class="flex items-center">
		{#if currency.position === 'left'}
			{@render symbol()}
		{/if}
		<input
			type="number"
			onchange={(e) => onInputChange('max', e)}
			step={1}
			min={minValue + 1}
			value={maxValue}
			{max}
			class={[
				'w-20 rounded border border-neutral-300 p-1',
				currency.position === 'left' ? 'pl-3' : 'pr-3',
			]}
		/>
		{#if currency.position === 'right'}
			{@render symbol()}
		{/if}
	</div>
</div>

<div class="sliders_control" bind:clientWidth>
	<input
		type="range"
		step={1}
		{min}
		{max}
		bind:value={minValue}
		oninput={() => onSliderInput('min')}
		onchange={onChange}
		id="minSlider"
	/>
	<input
		type="range"
		step={1}
		{min}
		{max}
		bind:value={maxValue}
		oninput={() => onSliderInput('max')}
		onchange={onChange}
		bind:this={toSlider}
	/>
</div>

{#snippet symbol()}
	<span class="text-neutral-500">{currency.symbol}</span>
{/snippet}

<style>
	.sliders_control {
		position: relative;
		--sliders-color: black;
	}

	input[type='range']::-webkit-slider-thumb {
		-webkit-appearance: none;
		pointer-events: all;
		width: 16px;
		height: 16px;
		background-color: white;
		border: var(--sliders-color) solid 0.25rem;
		border-radius: 50%;
		cursor: pointer;
	}

	input[type='range']::-moz-range-thumb {
		-webkit-appearance: none;
		appearance: none;
		pointer-events: all;
		width: 8px;
		height: 8px;
		background-color: white;
		border: var(--sliders-color) solid 0.25rem;
		border-radius: 50%;
		cursor: pointer;
	}

	input[type='range'] {
		-webkit-appearance: none;
		appearance: none;
		height: 3px;
		width: 100%;
		position: absolute;
		background-color: #aaaaaa;
		pointer-events: none;
	}

	input[type='number']::-webkit-outer-spin-button,
	input[type='number']::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	input[type='number'] {
		appearance: textfield;
		-moz-appearance: textfield; /* Firefox */
		-webkit-appearance: textfield; /* Safari, old Chrome */
	}

	#minSlider {
		height: 0;
		z-index: 1;
		margin-top: 1.5px;
	}
</style>
